/**
 * BuildChip — the build label, everywhere a team is listed.
 *
 * 🔴 IT IS MONOCHROME ON PURPOSE, AND THAT IS THE SCOPE LINE MADE VISIBLE.
 * Colour ranks things. A green "Zero RB" beside an amber "Balanced" would tell a
 * reader one build is better than the other, which is a claim this product
 * deliberately does not make — the outcome layer is withheld, so the chip must
 * not smuggle it back in through a palette. Every build wears the same quiet
 * outline, and the only thing the chip asserts is what the money did.
 *
 * It also keeps GroupCard's standing rule intact: grade colour lives in the
 * badge and nowhere else. A second coloured token on the same row is one more
 * colour system than a reader can hold.
 *
 * 🔴 ABSENT, NEVER GUESSED. No build, no chip. A roster typed without dollars
 * has no shape to measure and gets nothing here rather than a "Balanced" it did
 * not earn — see buildArchetype.js, which returns null for exactly that case.
 *
 * 🔴 AND THE REMAINDER WEARS NOTHING EITHER (Sep 3 2026). The chip names a
 * DISTINCTIVE shape. Balanced is the absence of one — buildArchetype.js's own
 * header calls it "none of the shapes above" and measures it at 70% of real
 * superflex teams and 76% of real 1QB teams — so a chip on it labels almost
 * every row in almost every room with the fact that nothing was found. A mark
 * most teams wear distinguishes nobody, and it crowds out the one case the chip
 * exists for: the row that actually did something particular. Same spirit as
 * absent-never-guessed, one step further in: the chip is drawn when there is
 * something to say and is silent otherwise.
 *
 * The classifier is UNTOUCHED. buildArchetype still returns BALANCED with its
 * evidence sentence, the room card's expansion still shows that sentence behind
 * its tap, and the label still travels to the analytics meta. What changed is
 * only what a list ROW draws, and it changed here so that every list — the
 * switcher, the room strip, the room cards — changed with it.
 */

import { theme } from "../../data/theme.js";
import { C, SZ } from "../Submit/submitStyles.js";

/**
 * buildArchetype.js's remainder key.
 *
 * 🔴 HAND-SYNC PACT, and a deliberately small one: BUILD_LABELS can say which
 * labels exist but not which of them is the remainder, so that fact lives here,
 * once, in the one file that acts on it. If the remainder is ever renamed in
 * buildArchetype.js, this line moves in the same commit or every row starts
 * wearing "Balanced" again.
 */
const REMAINDER_KEY = "BALANCED";

/**
 * Does this build get a chip at all?
 *
 * Exported so a caller that wraps the chip in its own element — the room card
 * puts it on a line of its own — can leave the wrapper out too, without a
 * second copy of the rule above deciding it.
 */
export function wearsBuildChip(build) {
  return !!(build && build.label && build.key !== REMAINDER_KEY);
}

export default function BuildChip({ build, dense, testId }) {
  if (!wearsBuildChip(build)) return null;
  return (
    <span
      data-testid={testId}
      data-build={build.key}
      title={build.evidence || undefined}
      style={{
        display: "inline-block",
        flexShrink: 0,
        padding: dense ? "1px 6px" : "2px 8px",
        borderRadius: theme.radius.sm,
        border: `1px solid ${C.border.default}`,
        background: C.bg.deep,
        color: C.text.secondary,
        fontSize: SZ.micro,
        fontWeight: 700,
        whiteSpace: "nowrap",
        lineHeight: 1.5,
      }}
    >
      {build.label}
    </span>
  );
}
